import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { AvatarWrapper } from '@/components/ui/avatar';
import { TableUser, TableUserContent, TableUserTitle } from '@/components/ui/table';
import { EmptyState } from '@/components/custom/empty-state';
import { ComboboxSingle } from '@/components/ui/combobox-single';
import { downloadReportFile } from '@/utils/downloadReportFile';
import { buildEvolutionChartData } from './chartData';
import { useReportSection } from './useReportSection';
import { SectionState } from './SectionState';
import { EvolutionChart } from './charts/EvolutionChart';
import { PerformanceBarRow, PerformanceLegend } from './PerformanceBarRow';
import { SchoolRankingChart } from './charts/SchoolRankingChart';
import { RankingTable, RankingPosition } from './RankingTable';
import { ReportTopBar } from './ReportTopBar';
import { formatScore, formatPercentage } from './formatters';

const BASE_PATH = '/reports/simulados_performance';
const ALL_QUESTION_BOOKS = 'todos';

export function GeneralTab() {
  const client = useClient();
  const [questionBookOptions, setQuestionBookOptions] = useState([{ value: ALL_QUESTION_BOOKS, label: 'Todos os simulados' }]);
  const [questionBookId, setQuestionBookId] = useState(ALL_QUESTION_BOOKS);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  useEffect(() => {
    let active = true;
    client.fetch('/question_books/simple?filter[visibility]=open').then(({ data, error }) => {
      if (!active) return;
      if (error) {
        toast.error('Falha ao carregar simulados');
        return;
      }
      setQuestionBookOptions([{ value: ALL_QUESTION_BOOKS, label: 'Todos os simulados' }, ...data.map(item => ({ value: String(item.id), label: item.title?.trim() }))]);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [questionBookId]);

  const query = questionBookId !== ALL_QUESTION_BOOKS ? `?question_book_id=${questionBookId}` : '';

  const kpisSection = useReportSection(() => client.fetch(`${BASE_PATH}/kpis${query}`), [questionBookId]);
  const evolutionSection = useReportSection(() => client.fetch(`${BASE_PATH}/evolution_chart${query}`), [questionBookId]);
  const distributionSection = useReportSection(() => client.fetch(`${BASE_PATH}/distribution_chart${query}`), [questionBookId]);
  const studentRankingSection = useReportSection(() => client.fetch(`${BASE_PATH}/student_ranking${query}`), [questionBookId]);
  const institutionRankingSection = useReportSection(() => client.fetch(`${BASE_PATH}/institution_ranking${query}`), [questionBookId]);

  const handleDownload = () => downloadReportFile(`${BASE_PATH}/export`, questionBookId !== ALL_QUESTION_BOOKS ? { question_book_id: questionBookId } : {});

  const evolution = evolutionSection.data ? buildEvolutionChartData(evolutionSection.data.chart_data, { labelKey: 'course' }) : null;
  const distributionBySeries = distributionSection.data?.chart_data?.map(item => ({
    label: item.course,
    average: item.score_percentage,
    low: item.low,
    mid: item.medium,
    high: item.high
  }));
  const schoolsRanking = institutionRankingSection.data?.ranking?.map(item => ({ label: item.name, average: item.score_percentage }));

  const studentColumns = [
    { key: 'schoolSeries', label: 'U.E/Série', render: row => `${row.institution} - ${row.course}` },
    { key: 'classroom', label: 'Turma' },
    { key: 'average', label: 'Média geral', align: 'right', render: row => formatScore(row.average_score) }
  ];

  return (
    <>
      <ReportTopBar
        lastUpdatedAt={lastUpdatedAt}
        onDownload={handleDownload}
      >
        <div className="w-[220px]">
          <ComboboxSingle
            options={questionBookOptions}
            value={questionBookId}
            onValueChange={setQuestionBookId}
          />
        </div>
      </ReportTopBar>

      <div className="report-widget">
        <div className="report-widget__header">
          <h3 className="report-widget__title">Visão geral</h3>
        </div>
        <div className="report-widget__body">
          <SectionState
            loading={kpisSection.loading}
            error={kpisSection.error}
            onRetry={kpisSection.reload}
            errorTitle="Não foi possível carregar os indicadores"
          >
            <div className="report-kpi-card-wrapper">
              <div className="report-kpi-card report-kpi-card--check">
                <h3 className="report-kpi-card__title">Alunos que participaram do simulado</h3>
                <p className="report-kpi-card__value">{kpisSection.data?.participants}</p>
              </div>
              <div className="report-kpi-card report-kpi-card--verified">
                <h3 className="report-kpi-card__title">Presença média</h3>
                <p className="report-kpi-card__value">{formatPercentage(kpisSection.data?.attendance_percentage)}</p>
              </div>
              <div className="report-kpi-card report-kpi-card--users">
                <h3 className="report-kpi-card__title">Média geral</h3>
                <p className="report-kpi-card__value">{formatScore(kpisSection.data?.average_score)}</p>
              </div>
            </div>
          </SectionState>
        </div>
      </div>

      <div className="report-widget">
        <div className="report-widget__header report-widget__header--column">
          <h3 className="report-widget__title">Evolução Série X Simulado</h3>
          <p className="report-widget__subtitle">% de acerto nos simulados</p>
        </div>
        <div className="report-widget__body">
          <SectionState
            loading={evolutionSection.loading}
            error={evolutionSection.error}
            onRetry={evolutionSection.reload}
            errorTitle="Não foi possível carregar o gráfico de evolução"
          >
            <EvolutionChart
              categories={evolution?.categories}
              series={evolution?.series}
            />
          </SectionState>
        </div>
      </div>

      <div className="report-widget">
        <div className="report-widget__header report-widget__header--column">
          <h3 className="report-widget__title">Distribuição de Desempenho Por Série</h3>
          <p className="report-widget__subtitle">Cada barra = 100% dos alunos da série, divididos por faixa de acerto</p>
        </div>
        <div className="report-widget__body">
          <SectionState
            loading={distributionSection.loading}
            error={distributionSection.error}
            onRetry={distributionSection.reload}
            errorTitle="Não foi possível carregar a distribuição de desempenho"
          >
            {!distributionBySeries?.length ? (
              <EmptyState
                type="data"
                variant="plain"
                text="Nenhum dado disponível"
              />
            ) : (
              <>
                <div className="flex flex-col gap-5">
                  {distributionBySeries.map(item => (
                    <PerformanceBarRow
                      key={item.label}
                      label={item.label}
                      average={item.average}
                      low={item.low}
                      mid={item.mid}
                      high={item.high}
                    />
                  ))}
                </div>
                <PerformanceLegend />
              </>
            )}
          </SectionState>
        </div>
      </div>

      <div className="report__section">
        <div
          className="report-widget"
          style={{ flex: '1.6 1 480px' }}
        >
          <div className="report-widget__header report-widget__header--column">
            <h3 className="report-widget__title">Ranking Geral: Top 10 Estudantes</h3>
            <p className="report-widget__subtitle">Média geral em todos os simulados</p>
          </div>
          <div className="report-widget__body">
            <SectionState
              loading={studentRankingSection.loading}
              error={studentRankingSection.error}
              onRetry={studentRankingSection.reload}
              errorTitle="Não foi possível carregar o ranking de estudantes"
            >
              <RankingTable
                rows={studentRankingSection.data?.ranking || []}
                columns={studentColumns}
                leadingLabel="Estudantes"
                leadingWidth={260}
                renderLeading={(row, index) => (
                  <TableUser>
                    <RankingPosition index={index} />
                    <AvatarWrapper
                      size="xs"
                      src={row.image}
                      text={row.name}
                    />
                    <TableUserContent>
                      <TableUserTitle>{row.name}</TableUserTitle>
                      <span className="w-full truncate text-[11px] text-neutral">{row.email}</span>
                    </TableUserContent>
                  </TableUser>
                )}
              />
            </SectionState>
          </div>
        </div>

        <div
          className="report-widget"
          style={{ flex: '1 1 320px' }}
        >
          <div className="report-widget__header report-widget__header--column">
            <h3 className="report-widget__title">Ranking Geral: Top 10 Escolas</h3>
            <p className="report-widget__subtitle">% de acerto em todos os simulados</p>
          </div>
          <div className="report-widget__body">
            <SectionState
              loading={institutionRankingSection.loading}
              error={institutionRankingSection.error}
              onRetry={institutionRankingSection.reload}
              errorTitle="Não foi possível carregar o ranking de escolas"
            >
              <SchoolRankingChart data={schoolsRanking || []} />
            </SectionState>
          </div>
        </div>
      </div>
    </>
  );
}
